import React, { useState, useEffect } from 'react';
import { usePIM } from '../context/PIMContext';
import { X, Save, AlertCircle } from 'lucide-react';

const UserModal = ({ user, onClose }) => {
    const { addUser, updateUser } = usePIM();
    const [formData, setFormData] = useState({
        username: '',
        email: '',
        password: '',
        role: 'Viewer',
        status: 'Active',
        web_agent_api_key: ''
    });
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (user) {
            setFormData({
                username: user.username,
                email: user.email,
                password: '', // Don't populate password for edit
                role: user.role,
                status: user.status,
                web_agent_api_key: user.web_agent_api_key || ''
            });
        }
    }, [user]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);

        try {
            let result;
            if (user) {
                // Update existing user
                const dataToUpdate = { ...formData };
                if (!dataToUpdate.password) delete dataToUpdate.password; // Only send password if changed
                result = await updateUser(user.id, dataToUpdate);
            } else {
                // Add new user
                if (!formData.password) {
                    setError('Password is required for new users.');
                    setLoading(false);
                    return;
                }
                result = await addUser(formData);
            }

            if (result.success) {
                onClose();
            } else {
                setError(result.message || 'Operation failed');
            }
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 backdrop-blur-sm">
            <div className="bg-stone-900 rounded-lg shadow-2xl border border-stone-700 w-full max-w-md overflow-hidden animate-in fade-in zoom-in duration-200">
                <div className="flex justify-between items-center p-4 border-b border-stone-800 bg-stone-950">
                    <h3 className="text-lg font-semibold text-stone-100">
                        {user ? 'Edit User' : 'Add New User'}
                    </h3>
                    <button onClick={onClose} className="text-stone-400 hover:text-white transition-colors">
                        <X size={20} />
                    </button>
                </div>

                <form onSubmit={handleSubmit} className="p-6 space-y-4">
                    {error && (
                        <div className="bg-red-900/30 border border-red-800 text-red-200 p-3 rounded-md flex items-center gap-2 text-sm">
                            <AlertCircle size={16} />
                            {error}
                        </div>
                    )}

                    <div>
                        <label className="block text-sm font-medium text-stone-400 mb-1">Username</label>
                        <input
                            type="text"
                            name="username"
                            value={formData.username}
                            onChange={handleChange}
                            required
                            className="w-full bg-stone-800 border border-stone-700 rounded-md p-2 text-stone-200 focus:outline-none focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                            placeholder="jdoe"
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-stone-400 mb-1">Email</label>
                        <input
                            type="email"
                            name="email"
                            value={formData.email}
                            onChange={handleChange}
                            required
                            className="w-full bg-stone-800 border border-stone-700 rounded-md p-2 text-stone-200 focus:outline-none focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                            placeholder="john@example.com"
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-stone-400 mb-1">
                            {user ? 'Password (leave blank to keep current)' : 'Password'}
                        </label>
                        <input
                            type="password"
                            name="password"
                            value={formData.password}
                            onChange={handleChange}
                            required={!user}
                            className="w-full bg-stone-800 border border-stone-700 rounded-md p-2 text-stone-200 focus:outline-none focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                            placeholder="••••••••"
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-stone-400 mb-1">Web Agent API Key</label>
                        <input
                            type="password"
                            name="web_agent_api_key"
                            value={formData.web_agent_api_key}
                            onChange={handleChange}
                            className="w-full bg-stone-800 border border-stone-700 rounded-md p-2 text-stone-200 focus:outline-none focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                            placeholder="Enter API Key"
                        />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-stone-400 mb-1">Role</label>
                            <select
                                name="role"
                                value={formData.role}
                                onChange={handleChange}
                                className="w-full bg-stone-800 border border-stone-700 rounded-md p-2 text-white focus:outline-none focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                            >
                                <option value="Admin">Admin</option>
                                <option value="Editor">Editor</option>
                                <option value="Viewer">Viewer</option>
                            </select>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-stone-400 mb-1">Status</label>
                            <select
                                name="status"
                                value={formData.status}
                                onChange={handleChange}
                                className="w-full bg-stone-800 border border-stone-700 rounded-md p-2 text-white focus:outline-none focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                            >
                                <option value="Active">Active</option>
                                <option value="Inactive">Inactive</option>
                            </select>
                        </div>
                    </div>

                    <div className="pt-4 flex justify-end gap-3">
                        <button
                            type="button"
                            onClick={onClose}
                            className="px-4 py-2 bg-stone-800 hover:bg-stone-700 text-stone-300 rounded-md transition-colors"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={loading}
                            className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            <Save size={18} />
                            {loading ? 'Saving...' : 'Save User'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default UserModal;
